import { useState, useRef, useEffect } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { clsx } from 'clsx'
import { verifyEmail, resendVerification } from '../api/auth'

export default function VerifyEmailPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const email = location.state?.email || ''

  const [code, setCode] = useState(['', '', '', '', '', ''])
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [resendCooldown, setResendCooldown] = useState(0)

  const inputRefs = useRef<(HTMLInputElement | null)[]>([])

  // Cooldown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  // Auto-focus first input
  useEffect(() => {
    inputRefs.current[0]?.focus()
  }, [])

  const handleChange = (index: number, value: string) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return

    const newCode = [...code]
    newCode[index] = value
    setCode(newCode)
    setError('')

    // Auto-advance to next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }

    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newCode.every(d => d)) {
      handleSubmit(newCode.join(''))
    }
  }

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Backspace: clear current or go back
    if (e.key === 'Backspace') {
      if (!code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus()
      }
    }
    // Arrow keys
    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowRight' && index < 5) {
      inputRefs.current[index + 1]?.focus()
    }
  }

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (pastedData.length === 6) {
      const newCode = pastedData.split('')
      setCode(newCode)
      inputRefs.current[5]?.focus()
      handleSubmit(pastedData)
    }
  }

  const handleSubmit = async (codeString?: string) => {
    const fullCode = codeString || code.join('')
    if (fullCode.length !== 6) {
      setError('Введите 6-значный код')
      return
    }

    setLoading(true)
    setError('')

    try {
      await verifyEmail(fullCode, email)
      setSuccess(true)
      setTimeout(() => navigate('/login', { state: { verified: true } }), 2000)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Неверный код')
      setCode(['', '', '', '', '', ''])
      inputRefs.current[0]?.focus()
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    if (!email || resendCooldown > 0) return

    setResending(true)
    setError('')

    try {
      await resendVerification(email)
      setResendCooldown(60)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Не удалось отправить код')
    } finally {
      setResending(false)
    }
  }

  if (success) {
    return (
      <div className="min-h-screen flex flex-col justify-center px-4 py-6 sm:px-6 sm:py-12 bg-gradient-calm">
        <div className="sm:mx-auto sm:w-full sm:max-w-md animate-fade-in">
          <div className="card-elevated py-8 px-6 sm:px-10 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-success-light rounded-full flex items-center justify-center">
              <svg className="w-8 h-8 text-success-dark" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-neutral-800 mb-2">Email подтверждён!</h2>
            <p className="text-neutral-500">Перенаправляем на страницу входа...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col justify-center px-4 py-6 sm:px-6 sm:py-12 bg-gradient-calm">
      <div className="sm:mx-auto sm:w-full sm:max-w-md animate-fade-in">
        {/* Logo */}
        <div className="flex justify-center mb-4 sm:mb-6">
          <div className="relative">
            <div className="absolute -inset-4 bg-primary-200 rounded-full blur-xl opacity-50" />
            <div className="relative w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl sm:rounded-3xl flex items-center justify-center shadow-primary">
              <svg className="w-8 h-8 sm:w-10 sm:h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
              </svg>
            </div>
          </div>
        </div>

        <h1 className="text-center text-2xl sm:text-3xl font-bold text-neutral-800">
          Подтверждение email
        </h1>
        <p className="mt-2 text-center text-sm sm:text-base text-neutral-500">
          {email ? (
            <>Введите код, отправленный на <span className="font-medium text-neutral-700">{email}</span></>
          ) : (
            'Введите 6-значный код из письма'
          )}
        </p>
      </div>

      <div className="mt-4 sm:mt-8 sm:mx-auto sm:w-full sm:max-w-md animate-slide-up">
        <div className="card-elevated py-6 px-5 sm:py-8 sm:px-10">
          {error && (
            <div className="mb-4 bg-error-light text-error-dark p-4 rounded-2xl text-sm flex items-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {error}
            </div>
          )}

          {/* 6-digit code input */}
          <div className="flex justify-center gap-2 sm:gap-3 mb-6">
            {code.map((digit, index) => (
              <input
                key={index}
                ref={el => { inputRefs.current[index] = el }}
                type="text"
                inputMode="numeric"
                maxLength={1}
                value={digit}
                onChange={e => handleChange(index, e.target.value)}
                onKeyDown={e => handleKeyDown(index, e)}
                onPaste={index === 0 ? handlePaste : undefined}
                disabled={loading}
                className={clsx(
                  'w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl font-bold rounded-xl border-2 transition-all',
                  'focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-400',
                  digit ? 'border-primary-300 bg-primary-50' : 'border-neutral-200 bg-white',
                  loading && 'opacity-50 cursor-wait'
                )}
              />
            ))}
          </div>

          <button
            onClick={() => handleSubmit()}
            disabled={loading || code.some(d => !d)}
            className={clsx(
              'btn-primary w-full text-base py-3 sm:py-3.5',
              (loading || code.some(d => !d)) && 'opacity-70 cursor-not-allowed'
            )}
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Проверяем...
              </span>
            ) : (
              'Подтвердить'
            )}
          </button>

          {/* Resend code */}
          {email && (
            <div className="mt-4 text-center">
              <button
                onClick={handleResend}
                disabled={resending || resendCooldown > 0}
                className={clsx(
                  'text-sm transition-colors',
                  resendCooldown > 0
                    ? 'text-neutral-400 cursor-not-allowed'
                    : 'text-primary-600 hover:text-primary-700'
                )}
              >
                {resending ? (
                  'Отправляем...'
                ) : resendCooldown > 0 ? (
                  `Отправить повторно (${resendCooldown}с)`
                ) : (
                  'Отправить код повторно'
                )}
              </button>
            </div>
          )}

          <p className="mt-6 text-center text-sm text-neutral-500">
            <Link to="/login" className="text-primary-600 hover:text-primary-700 font-medium transition-colors">
              Вернуться к входу
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
